import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Menu, Transition } from '@headlessui/react';
import { client } from '../axiosClient';
import { useToast } from './ui/use-toast';
import { Button } from './ui/button';
import { XMarkIcon, AdjustmentsHorizontalIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { useStartTour } from '../hooks/startTour';
import Joyride from 'react-joyride';

// Reusable Category Filter Component - Displays and handles category filtering
const CategoryFilter = memo(({ categories, selectedCategory, onSelect, className }) => (
  <div className={`${className} flex flex-wrap gap-2 mb-6`}>
    {categories.map((category) => (
      <button
        key={category}
        onClick={() => onSelect(category)}
        id={`category_filter_button_${category}`}
        className={`px-4 py-2 rounded-full border-2 transition-all duration-200 ${
          selectedCategory === category
            ? 'border-purple-600 bg-purple-600 text-white'
            : 'border-gray-300 hover:border-purple-600'
        }`}
      >
        {category}
      </button>
    ))}
  </div>
));

CategoryFilter.displayName = 'CategoryFilter';

// Reusable Grid Size Selector Component - Controls the grid layout size
const GridSizeSelector = memo(({ gridSize, onGridSizeChange, className }) => {
  const sizes = [2, 3, 4, 6];
  
  return (
    <div className={`${className} flex items-center gap-2`}>
      <select
        value={gridSize}
        onChange={(e) => onGridSizeChange(Number(e.target.value))}
        id={`grid_size_selector_${gridSize}`}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-lg "
      >
        {sizes.map(size => (
          <option key={size} value={size}>
            {size}x{size}
          </option>
        ))}
      </select>
    </div>
  );
});

// Filter Button Component - Handles advanced filtering options
const FilterButton = memo(({ className, colors, seasons, activities, brands, selectedColor, selectedSeason, selectedActivity, selectedBrand, onFilterChange }) => {
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState('right');
  const menuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (menuRef.current) {
        const rect = menuRef.current.getBoundingClientRect();
        const spaceOnRight = window.innerWidth - rect.right;
        const spaceOnLeft = rect.left;
        
        setSubmenuPosition(spaceOnRight > 200 ? 'right' : spaceOnLeft > 200 ? 'left' : 'bottom');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuItemClick = (e, menuType) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveSubmenu(menuType);
  };

  return (
    <Menu as="div" className={`${className} relative`} ref={menuRef}>
      {({ open }) => (
        <>
          <Menu.Button className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full border border-gray-300 hover:bg-gray-50 text-sm sm:text-base">
            <AdjustmentsHorizontalIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        <span>Filter</span>
      </Menu.Button>
      <Transition
            show={open}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-in"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
            <Menu.Items static className="absolute mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 w-48 sm:w-56">
              <div className="py-1">
                {/* Main menu items */}
                <div>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active || activeSubmenu === 'color' ? 'bg-gray-100' : ''
                        } flex w-full items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-700`}
                        onMouseEnter={() => setActiveSubmenu('color')}
                        onClick={(e) => handleMenuItemClick(e, 'color')}
                      >
                        <span>Color</span>
                        <svg className={`h-4 w-4 transform ${submenuPosition === 'bottom' ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active || activeSubmenu === 'season' ? 'bg-gray-100' : ''
                        } flex w-full items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-700`}
                        onMouseEnter={() => setActiveSubmenu('season')}
                        onClick={(e) => handleMenuItemClick(e, 'season')}
                      >
                        <span>Season</span>
                        <svg className={`h-4 w-4 transform ${submenuPosition === 'bottom' ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active || activeSubmenu === 'activity' ? 'bg-gray-100' : ''
                        } flex w-full items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-700`}
                        onMouseEnter={() => setActiveSubmenu('activity')}
                        onClick={(e) => handleMenuItemClick(e, 'activity')}
                      >
                        <span>Activity</span>
                        <svg className={`h-4 w-4 transform ${submenuPosition === 'bottom' ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active || activeSubmenu === 'brand' ? 'bg-gray-100' : ''
                        } flex w-full items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-700`}
                        onMouseEnter={() => setActiveSubmenu('brand')}
                        onClick={(e) => handleMenuItemClick(e, 'brand')}
                      >
                        <span>Brand</span>
                        <svg className={`h-4 w-4 transform ${submenuPosition === 'bottom' ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </Menu.Item>
                </div>

                {/* Submenu with dynamic positioning */}
                {activeSubmenu && (
                  <div 
                    className={`
                      ${submenuPosition === 'right' ? 'absolute left-full top-0 ml-1' : 
                        submenuPosition === 'left' ? 'absolute right-full top-0 mr-1' : 
                        'relative mt-1 ml-4'
                      }
                      w-48 sm:w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5
                    `}
                    onMouseLeave={() => submenuPosition !== 'bottom' && setActiveSubmenu(null)}
                  >
                    <div className="py-1 max-h-48 sm:max-h-64 overflow-y-auto">
                      {activeSubmenu === 'color' && colors.map((color) => (
                        <Menu.Item key={color}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onFilterChange('color', color);
                                setActiveSubmenu(null);
                              }}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } ${
                                selectedColor === color ? 'text-purple-600 font-medium' : 'text-gray-700'
                              } group flex w-full items-center px-3 py-1.5 sm:px-4 sm:py-2 text-sm`}
                            >
                              {color}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                      {activeSubmenu === 'season' && seasons.map((season) => (
                        <Menu.Item key={season}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onFilterChange('season', season);
                                setActiveSubmenu(null);
                              }}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } ${
                                selectedSeason === season ? 'text-purple-600 font-medium' : 'text-gray-700'
                              } group flex w-full items-center px-3 py-1.5 sm:px-4 sm:py-2 text-sm`}
                            >
                              {season}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                      {activeSubmenu === 'activity' && activities.map((activity) => (
                        <Menu.Item key={activity}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onFilterChange('activity', activity);
                                setActiveSubmenu(null);
                              }}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } ${
                                selectedActivity === activity ? 'text-purple-600 font-medium' : 'text-gray-700'
                              } group flex w-full items-center px-3 py-1.5 sm:px-4 sm:py-2 text-sm`}
                            >
                              {activity}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                      {activeSubmenu === 'brand' && brands.map((brand) => (
                        <Menu.Item key={brand}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onFilterChange('brand', brand);
                                setActiveSubmenu(null);
                              }}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } ${
                                selectedBrand === brand ? 'text-purple-600 font-medium' : 'text-gray-700'
                              } group flex w-full items-center px-3 py-1.5 sm:px-4 sm:py-2 text-sm`}
                            >
                              {brand}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </div>
                )}
              </div>
        </Menu.Items>
      </Transition>
        </>
      )}
    </Menu>
  );
});

// Active Filters Component - Shows currently applied filters
const ActiveFilters = memo(({ selectedColor, selectedSeason, selectedActivity, selectedBrand, onRemove }) => {
  const filters = [
    { type: 'color', value: selectedColor },
    { type: 'season', value: selectedSeason },
    { type: 'activity', value: selectedActivity },
    { type: 'brand', value: selectedBrand }
  ].filter(filter => filter.value !== 'All');

  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {filters.map(({ type, value }) => (
        <button
          key={`${type}-${value}`}
          onClick={() => onRemove(type)}
          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm hover:bg-purple-100 transition-colors"
        >
          <span className="capitalize">{value}</span>
          <XCircleIcon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
});

ActiveFilters.displayName = 'ActiveFilters';

// Main OutfitGallery Component - Manages the outfit gallery view and interactions
const OutfitGallery = ({ refreshTrigger }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [outfits, setOutfits] = useState([]);
  const [filteredOutfits, setFilteredOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const { runTour, steps, startTour, config, pushToDataLayer } = useStartTour('outfitGallery');
  
  // Add ref for scroll restoration
  const scrollRestorationRef = useRef({
    shouldRestore: false,
    position: 0
  });

  // Initialize gridSize from localStorage or default to 4
  const [gridSize, setGridSize] = useState(() => {
    const savedGridSize = localStorage.getItem('gridSize');
    return savedGridSize ? Number(savedGridSize) : 4;
  });

  // Update localStorage whenever gridSize changes
  const handleGridSizeChange = useCallback((size) => {
    localStorage.setItem('gridSize', size);
    setGridSize(size);
  }, []);

  // Filter states
  const [categories, setCategories] = useState(['All']);
  const [colors, setColors] = useState(['All']);
  const [seasons, setSeasons] = useState(['All']);
  const [activities, setActivities] = useState(['All']);
  const [brands, setBrands] = useState(['All']);
  
  // Initialize filter states from localStorage or default to 'All'
  const [selectedCategory, setSelectedCategory] = useState(() => 
    localStorage.getItem('selectedCategory') || 'All'
  );
  const [selectedColor, setSelectedColor] = useState(() => 
    localStorage.getItem('selectedColor') || 'All'
  );
  const [selectedSeason, setSelectedSeason] = useState(() => 
    localStorage.getItem('selectedSeason') || 'All'
  );
  const [selectedActivity, setSelectedActivity] = useState(() => 
    localStorage.getItem('selectedActivity') || 'All'
  );
  const [selectedBrand, setSelectedBrand] = useState(() => 
    localStorage.getItem('selectedBrand') || 'All'
  );
  
  const { toast } = useToast();

  // Save filter states to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('selectedCategory', selectedCategory);
    localStorage.setItem('selectedColor', selectedColor);
    localStorage.setItem('selectedSeason', selectedSeason);
    localStorage.setItem('selectedActivity', selectedActivity);
    localStorage.setItem('selectedBrand', selectedBrand);
  }, [selectedCategory, selectedColor, selectedSeason, selectedActivity, selectedBrand]);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenOutfitGalleryTour');
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem('hasSeenOutfitGalleryTour', 'false');
    }
  }, [startTour]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, step, type } = data;

    // Track tour interactions
    if (type === 'step:after' || type === 'tour:end' || type === 'tour:start') {
      pushToDataLayer(
        action,
        status,
        index + 1,
        step?.target || 'body'
      );
    }

    if (status === 'finished' || status === 'skipped') {
      localStorage.setItem('hasSeenOutfitGalleryTour', 'true');
    }
  };

  const fetchOutfits = useCallback(async () => {
    try {
      const response = await client.get('/api/outfits/');
      setOutfits(response.data);
      
      // Extract unique filter options
      const categorySet = new Set(['All', ...response.data.map(outfit => outfit.category?.name).filter(Boolean)]);
      const colorSet = new Set(['All', ...response.data.map(outfit => outfit.color?.name).filter(Boolean)]);
      const seasonSet = new Set(['All', ...response.data.flatMap(outfit => outfit.season.map(s => s.name))]);
      const activitySet = new Set(['All', ...response.data.flatMap(outfit => outfit.activity.map(a => a.name))]);
      const brandSet = new Set(['All', ...response.data.map(outfit => outfit.brand?.name).filter(Boolean)]);
      
      setCategories(Array.from(categorySet));
      setColors(Array.from(colorSet));
      setSeasons(Array.from(seasonSet));
      setActivities(Array.from(activitySet));
      setBrands(Array.from(brandSet));
      
      setFilteredOutfits(response.data);
    } catch (error) {
      console.error('Error fetching outfits:', error);
      toast({
        title: "Error",
        description: "Failed to load outfits",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const filterOutfits = useCallback(() => {
    let filtered = outfits;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(outfit => outfit.category?.name === selectedCategory);
    }
    if (selectedColor !== 'All') {
      filtered = filtered.filter(outfit => outfit.color?.name === selectedColor);
    }
    if (selectedSeason !== 'All') {
      filtered = filtered.filter(outfit => outfit.season.some(s => s.name === selectedSeason));
    }
    if (selectedActivity !== 'All') {
      filtered = filtered.filter(outfit => outfit.activity.some(a => a.name === selectedActivity));
    }
    if (selectedBrand !== 'All') {
      filtered = filtered.filter(outfit => outfit.brand?.name === selectedBrand);
    }
    
    setFilteredOutfits(filtered);
  }, [outfits, selectedCategory, selectedColor, selectedSeason, selectedActivity, selectedBrand]);

  const handleFilterChange = useCallback((type, value) => {
    switch (type) {
      case 'color':
        setSelectedColor(value);
        break;
      case 'season':
        setSelectedSeason(value);
        break;
      case 'activity':
        setSelectedActivity(value);
        break;
      case 'brand':
        setSelectedBrand(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    fetchOutfits();
  }, [fetchOutfits, refreshTrigger]);

  useEffect(() => {
    filterOutfits();
  }, [filterOutfits, selectedCategory, selectedColor, selectedSeason, selectedActivity, selectedBrand]);

  useEffect(() => {
    // If any outfit is still processing, set up polling to refresh outfits every 5 seconds.
    if (outfits.some(outfit => outfit.processing_status === 'processing')) {
      const pollingInterval = setInterval(() => {
        fetchOutfits();
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(pollingInterval);
    }
  }, [outfits, fetchOutfits]);

  const handleDelete = useCallback(async (ids) => {
    try {
      if (Array.isArray(ids)) {
        await client.post('/api/outfits/delete-multiple/', { ids });
      } else {
        await client.delete(`/api/outfits/${ids}/`);
      }
      fetchOutfits();
      setIsSelecting(false);
      setSelectedItems(new Set());
      toast({
        title: "Success",
        description: "Items deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting outfits:', error);
      toast({
        title: "Error",
        description: "Failed to delete items",
        variant: "destructive",
      });
    }
  }, [fetchOutfits, toast]);

  const toggleItemSelection = useCallback((id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const gridSizeClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    6: 'grid-cols-6'
  }[gridSize];

  const handleRemoveFilter = useCallback((type) => {
    handleFilterChange(type, 'All');
  }, [handleFilterChange]);

  const handleDeleteSelected = useCallback(async () => {
    setIsDeleteModalOpen(false);
    await handleDelete(Array.from(selectedItems));
  }, [selectedItems, handleDelete]);

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // If all items are selected, deselect all
      setSelectedItems(new Set());
      setIsAllSelected(false);
    } else {
      // Select all filtered outfits
      const newSelectedItems = new Set(filteredOutfits.map(outfit => outfit.id));
      setSelectedItems(newSelectedItems);
      setIsAllSelected(true);
    }
  }, [filteredOutfits, isAllSelected]);

  // Update isAllSelected when selectedItems changes
  useEffect(() => {
    setIsAllSelected(selectedItems.size === filteredOutfits.length && selectedItems.size > 0);
  }, [selectedItems, filteredOutfits]);

  // Add a ref for the container
  const containerRef = useRef(null);

  // Check for saved scroll position on mount
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('outfitGalleryScrollPosition');
    if (savedScrollPosition !== null) {
      scrollRestorationRef.current = {
        shouldRestore: true,
        position: parseInt(savedScrollPosition, 10)
      };
      sessionStorage.removeItem('outfitGalleryScrollPosition');
    }
  }, []);

  // Restore scroll position after outfits are loaded and filtered
  useEffect(() => {
    if (!loading && scrollRestorationRef.current.shouldRestore) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollRestorationRef.current.position);
        // Reset the ref after restoration
        scrollRestorationRef.current = {
          shouldRestore: false,
          position: 0
        };
      });
    }
  }, [loading, filteredOutfits]);

  // Save scroll position before navigating to detail
  const handleOutfitClick = useCallback((outfitId) => {
    if (!isSelecting) {
      const scrollPosition = window.scrollY;
      sessionStorage.setItem('outfitGalleryScrollPosition', scrollPosition.toString());
      navigate(`/my-closet/${outfitId}`);
    }
  }, [isSelecting, navigate]);

  return (
    <div className="container mx-auto px-4 py-8" ref={containerRef}>
      {/* Tour Guide Integration */}
      <Joyride
        {...config}
        run={runTour}
        steps={steps}
        callback={handleJoyrideCallback}
      />

      {/* Gallery Header Section */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">My Closet</h1>
      </div>

      {/* Filter and Grid Size Selector Section*/}
      <div className="relative min-h-screen pb-24">
        <div className=" flex items-center gap-4 mb-4">
          <FilterButton
            className="filter-button"
            colors={colors}
            seasons={seasons}
            activities={activities}
            brands={brands}
            selectedColor={selectedColor}
            selectedSeason={selectedSeason}
            selectedActivity={selectedActivity}
            selectedBrand={selectedBrand}
            onFilterChange={handleFilterChange}
          />
          <GridSizeSelector 
            gridSize={gridSize} 
            onGridSizeChange={handleGridSizeChange} 
            className="grid-size-selector" 
          />
          <div className="flex items-center gap-2">
            <Button
              className="select-multiple-button"
              id="select-multiple-button"
              onClick={() => {
                setIsSelecting(!isSelecting);
                setSelectedItems(new Set());
                setIsAllSelected(false);
              }}
            >
              {isSelecting ? 'Cancel' : 'Select'}
            </Button>
            {isSelecting && (
              <Button
                className="select-all-button"
                id="select-all-button"
                variant={isAllSelected ? "secondary" : "outline"}
                onClick={handleSelectAll}
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </div>

        {/* Category Filter Section */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
          className="category-filter"
        />

        {/* Active Filters Display */}
        <ActiveFilters
          selectedColor={selectedColor}
          selectedSeason={selectedSeason}
          selectedActivity={selectedActivity}
          selectedBrand={selectedBrand}
          onRemove={handleRemoveFilter}
        />

        {/* Outfit Grid Section */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredOutfits.length === 0 ? (
          <div className="closet-empty-view text-center py-12">
            <h2 className="text-lg font-semibold">Your closet is empty!</h2>
            <Link to="/upload" className="text-blue-500"
            id="outfit-gallery-add-outfit-link">Add some outfits to get started →</Link>
          </div>
        ) : (
          <div className={`grid ${gridSizeClass} gap-2`}>
            {filteredOutfits.map((outfit) => (
              <motion.div
                key={outfit.id}
                layout
                className="relative"
              >
                <div
                  className={`group relative aspect-square bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer
                    ${isSelecting ? 'hover:opacity-80' : 'hover:shadow-xl'}`}
                  onClick={() => {
                    if (isSelecting) {
                      toggleItemSelection(outfit.id);
                    } else {
                      handleOutfitClick(outfit.id);
                    }
                  }}
                >
                  {outfit.processing_status === 'completed' ? (
                    <div id="closet-outfit-image" className="absolute inset-0 p-1 sm:p-2">
                      <img
                        id="closet-outfit-image"
                        src={outfit.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 p-1 flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
                      {outfit.processing_status === 'failed' ? (
                        <>
                          <div className="absolute inset-0 p-1 z-1">
                            <img
                              src={outfit.image_url || outfit.original_image}
                              alt=""
                              className="w-full h-full object-cover opacity-35"
                            />
                          </div>
                          <div className="text-red-500 sm:mb-2">
                            <svg className="w-6 h-6 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <p className="text-[10px] sm:text-xl text-center text-red-600 font-medium">Failed</p>
                          <p className="text-[7px] sm:text-lg text-center text-red-500">Upload again</p>
                        </>
                      ) : outfit.processing_status === 'processing' ? (
                        <>
                          <div className="animate-spin text-purple-600 mt-1 sm:mt-2">
                            <svg className="w-10 h-10 sm:w-12 sm:h-12" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                          <p className="text-xs mt-1 sm:text-sm text-center text-purple-600 font-medium">Analyzing Image</p>
                        </>
                      ) : (
                        <>
                          <div className="text-blue-500 mt-1 sm:mt-2">
                            <svg className="w-10 h-10 sm:w-12 sm:h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13l-3 3m0 0l-3-3m3 3V8" className="animate-pulse" />
                            </svg>
                          </div>
                          <p className="text-xs sm:text-sm text-center text-blue-600 font-medium">Preparing Image</p>
                        </>
                      )}
                    </div>
                  )}
                  {isSelecting && (
                    <div className={`absolute inset-0 flex items-center justify-center
                      ${selectedItems.has(outfit.id) ? 'bg-purple-600/40' : 'bg-black/20'}`}
                    >
                      {selectedItems.has(outfit.id) && (
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Sticky Delete Button */}
        {isSelecting && selectedItems.size > 0 && (
          <div className="sticky bottom-20 left-0 right-0 flex justify-center z-50 m-8">
            <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  className="px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 bg-opacity-90 backdrop-blur-sm"
                  id="delete-selected-items-button"
                >
                  Delete ({selectedItems.size})
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md w-[95vw] sm:w-full mx-auto">
                <DialogHeader>
                  <DialogTitle>Delete Selected {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-gray-600 text-sm sm:text-base">
                    Selected items will also be removed from generated outfits.<br/> <br/> Which cause missing parts in the generated outfits.<br/> <br/> This can't be undone.
                  </p>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    id="delete-selected-items-cancel-button"
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="px-4 py-2 text-sm sm:text-base text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    id="delete-selected-items-confirm-button"
                    onClick={handleDeleteSelected}
                    className="px-4 py-2 text-sm sm:text-base bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Sticky Add Outfit Button */}
        <div className={`sticky bottom-0 left-0 right-0 flex justify-center z-40 `}>
          <Link
            id="add-outfit-button"
            to="/upload"
            className="add-outfit-button inline-flex items-center gap-2 px-6 py-3 m-4 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full bg-opacity-90 backdrop-blur-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Outfit
          </Link>
        </div>
      </div>
    </div>
  );
};

export default memo(OutfitGallery);
